import { cardReadColumns, cardVisibleTo, learnerCards } from '../db/question-card-triggers'
import { readableRowsOptions } from '../dead-letter-queue/item-readers'
import { useMutation, useQuery } from '@w/react-query'
import { queryOptions } from '@w/react-query/db'
import { Button } from '@w/ui/components/button'
import { acceptCard } from '../db/learner-actions'
import { writeWorkspace } from '../db/workspace-write'
import { useRouterContext, useNavigate } from '../router/flashcards-fe.router-bindings'

/** Lifecycle management never renders inaccessible source content. */
export function CardManagement({ workspaceId }: { workspaceId: string }) {
  const ctx = useRouterContext()
  const navigate = useNavigate()
  const resource = ctx.spaces.get(workspaceId)
  const db = resource.db
  const cards = useQuery(readableRowsOptions(resource, db.query`SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(resource.userId)}
    WHERE ${cardVisibleTo(db.flashcards, resource.userId)} 
    ORDER BY ${db.flashcards.qid}`, db.flashcards.__selectSchema, 'card', 'CARD_DATA'), resource.queryClient)
  const accept = useMutation({ mutationFn: (qid: string) => {
    return writeWorkspace(ctx, workspaceId, () => acceptCard(db, {
      userId: resource.userId, deviceId: ctx.deviceId, qid,
    }))
  } })
  const pages = useQuery(queryOptions(db.query`SELECT ${db.pages.id} FROM ${db.pages}`, { id: db.pages.id }), resource.queryClient)
  const readable = new Set(pages.data?.map(page => page.id) ?? [])
  const unavailable = pages.data ? cards.data?.filter(card => !readable.has(card.pageId)) ?? [] : []
  const deleted = cards.data?.filter(card => card.touchedAt !== null && card.deletedBy !== null && card.deletedBy !== resource.userId) ?? []
  if (cards.error) return <p role="alert">Card management unavailable: {cards.error.message}</p>
  return <section aria-label="Card management" className="flex flex-col gap-4 p-4">
    <h2>Deleted-card review ({deleted.length})</h2>
    {deleted.map(card => <Button key={card.qid} data-testid={`card-review-${card.qid}`} onClick={() => {
      navigate({ to: '/$workspaceId/training/$flashcardId', params: { workspaceId, flashcardId: card.qid },
        state: { resolution: 'deleted-review', mark: -1 } })
    }}>Review deleted card</Button>)}
    {unavailable.length > 0 ? <h2>Source unavailable</h2> : null}
    {unavailable.map(card => <div key={card.qid} data-testid={`card-source-unavailable-${card.qid}`} className="border-separator flex flex-col gap-3 rounded-lg border p-3">
      <p>Source unavailable</p>
      <div className="flex flex-wrap gap-3">
        <Button data-testid={`card-source-accept-${card.qid}`} disabled={card.touchedAt === null || card.deletedBy === null || card.deletedBy === resource.userId} loading={accept.isPending} onClick={() => { accept.mutate(card.qid) }}>Accept / dismiss</Button>
        <Button data-testid={`card-source-clone-${card.qid}`} disabled>Clone</Button>
      </div>
    </div>)}
    {accept.error ? <p role="alert">{accept.error.message}</p> : null}
  </section>
}
